/**
 * useExamPermissions — batch-check multiple exam-service permissions.
 *
 * v4 note: JWT-only, like `useExamPermission`. See that file for why.
 */
import { useMemo } from 'react';
import { useExamAuth } from '@/hooks/useExamAuth';

interface ExamPermissionsResult {
  results: Record<string, boolean>;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  isAllowed: (permission: string) => boolean;
  allowedCount: number;
  deniedCount: number;
}

function matchesPermission(userPerms: string[], required: string): boolean {
  if (userPerms.includes(required)) return true;
  if (userPerms.includes('*:*:*')) return true;
  if (userPerms.includes('*:*:*:*')) return true;
  return false;
}

export function useExamPermissions(
  permissionList: string[],
): ExamPermissionsResult {
  const { roles, permissions, isLoading } = useExamAuth();

  const results = useMemo(() => {
    if (isLoading) return {} as Record<string, boolean>;
    const isSuper =
      roles.includes('system_admin') ||
      roles.includes('super_admin') ||
      permissions.includes('*:*:*') ||
      permissions.includes('*:*:*:*');
    const out: Record<string, boolean> = {};
    for (const p of permissionList) {
      out[p] = isSuper ? true : matchesPermission(permissions, p);
    }
    return out;
  }, [isLoading, roles, permissions, permissionList]);

  const allowedCount = Object.values(results).filter(Boolean).length;
  const deniedCount = Object.values(results).filter((v) => !v).length;

  return {
    results,
    loading: isLoading,
    error: null,
    refresh: () => {},
    isAllowed: (p: string) => results[p] ?? false,
    allowedCount,
    deniedCount,
  };
}
